import { debounce } from 'lodash';
import { useMemo, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TextField from '@mui/material/TextField';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { IContactListItem } from 'src/services/contacts/contacts.types';
import { PresentationExport } from 'src/services/presentations/presentations.types';
import { useGetOrganizationContactsLeanQuery } from 'src/services/contacts/contacts.service';
import { useSendPresentationMarketingMutation } from 'src/services/presentations/presentations.service';

// ----------------------------------------------------------------------

/** The most contacts in one send (backend MAX_MARKETING_CONTACTS). */
const MAX_CONTACTS = 50;
/** BusinessErrors.ActiveEmailConnectionNotAvailable: the recruiter hasn't connected their mailbox. */
const NO_EMAIL_CONNECTION = '20_0001';

const escapeHtml = (text: string) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/** The message as the group mailer takes it: each paragraph its own <p>, nothing typed taken as markup. */
export const messageToHtml = (text: string) => text.split(/\n{2,}/).map((paragraph) => `<p>${escapeHtml(paragraph.trim()).replace(/\n/g, '<br>')}</p>`).join('');

interface Props {
  presentationId: string;
  exported: PresentationExport;
  onClose: VoidFunction;
}

/**
 * Candidate marketing (JT1-1887, C13): an anonymised export emailed to client contacts the recruiter
 * picks, from their own connected mailbox, with the file attached.
 */
export default function SendMarketingDialog({ presentationId, exported, onClose }: Props) {
  const { t } = useTranslate();
  const { organization } = useAuthContext();
  const [search, setSearch] = useState('');
  const [contacts, setContacts] = useState<IContactListItem[]>([]);
  const [subject, setSubject] = useState(t('presentations.marketing.default_subject'));
  const [message, setMessage] = useState(t('presentations.marketing.default_body'));
  const [noConnection, setNoConnection] = useState(false);

  const { currentData: results, isFetching } = useGetOrganizationContactsLeanQuery(
    { org_id: organization?.id as string, page: { page: 1, per_page: 10 }, search_query: search },
    { skip: !organization?.id },
  );
  const [send, { isLoading }] = useSendPresentationMarketingMutation();

  const onSearch = useMemo(() => debounce((value: string) => setSearch(value), 400), []);
  // Only contacts with an email address can be sent to.
  const options = (results?.results ?? []).filter((contact) => !!contact.email);
  const label = (contact: IContactListItem) => [[contact.first_name, contact.last_name].filter(Boolean).join(' '), contact.company_name].filter(Boolean).join(' · ');

  const handleSend = async () => {
    try {
      await send({ id: presentationId, exportId: exported.id, contact_ids: contacts.map((contact) => contact.id), subject: subject.trim(), body: messageToHtml(message) }).unwrap();
      enqueueSnackbar(t('presentations.marketing.sent', { count: contacts.length }), { variant: 'success' });
      onClose();
    } catch (error) {
      if ((error as { data?: { error_code?: string } })?.data?.error_code === NO_EMAIL_CONNECTION) {
        setNoConnection(true);
      } else {
        enqueueSnackbar(t('presentations.marketing.failed'), { variant: 'error' });
      }
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('presentations.marketing.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">{t('presentations.marketing.attached', { name: exported.file_name })}</Alert>
          {noConnection && <Alert severity="warning">{t('presentations.marketing.no_connection')}</Alert>}
          <Autocomplete
            multiple
            options={options}
            value={contacts}
            loading={isFetching}
            filterOptions={(items) => items}
            getOptionLabel={label}
            getOptionDisabled={() => contacts.length >= MAX_CONTACTS}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setContacts(value)}
            onInputChange={(_event, value, reason) => reason === 'input' && onSearch(value)}
            renderInput={(params) => <TextField {...params} label={t('presentations.marketing.contacts')} helperText={t('presentations.marketing.contacts_helper', { count: MAX_CONTACTS })} />}
          />
          <TextField fullWidth label={t('presentations.marketing.subject')} value={subject} onChange={(event) => setSubject(event.target.value)} inputProps={{ maxLength: 255 }} />
          <TextField fullWidth multiline minRows={5} label={t('presentations.marketing.message')} value={message} onChange={(event) => setMessage(event.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <LoadingButton variant="contained" loading={isLoading} disabled={!contacts.length || !subject.trim() || !message.trim()} onClick={handleSend}>
          {t('presentations.marketing.send', { count: contacts.length })}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
